import { useState } from 'react';

import Icon from './Icon.jsx';

import { PATHS } from '../data/books.js';
import { isPathLocked } from '../lib/tiers.js';

/**
 * Book-by-book learning paths, derived from the verse deck (see data/books.js)
 * so a path can never be empty or out of sync with the content.
 */
export default function PathsScreen({ learning, isPremium, onStartDeck, onUpgrade }) {
  const [testament, setTestament] = useState('all');

  const paths = PATHS.filter((p) => testament === 'all' || p.testament === testament);

  return (
    <section className="screen">
      <div className="screen__heading">
        <h2>Learning paths</h2>
        <p>Work through a single book, verse by verse.</p>
      </div>

      <div className="segmented" role="tablist" aria-label="Filter by testament">
        {[
          { id: 'all', label: 'All' },
          { id: 'old', label: 'Old Testament' },
          { id: 'new', label: 'New Testament' },
        ].map((option) => (
          <button
            key={option.id}
            type="button"
            role="tab"
            aria-selected={testament === option.id}
            className={`segmented__btn${testament === option.id ? ' is-active' : ''}`}
            onClick={() => setTestament(option.id)}
          >
            {option.label}
          </button>
        ))}
      </div>

      <ul className="paths">
        {paths.map((path) => {
          // Lock position is measured against the full canon order, not the
          // filtered view, so switching filters can't unlock a path.
          const locked = isPathLocked(PATHS.indexOf(path), isPremium);
          const known = path.verseIds.filter((id) => learning[id] === 'known').length;
          const pct = Math.round((known / path.count) * 100);

          return (
            <li key={path.book}>
              <button
                type="button"
                className={`path${locked ? ' is-locked' : ''}`}
                onClick={() =>
                  locked ? onUpgrade() : onStartDeck({ type: 'book', book: path.book })
                }
              >
                <div className="path__top">
                  <span className="path__name">{path.book}</span>
                  <span className={`path__badge path__badge--${path.testament}`}>
                    {path.testament === 'old' ? 'OT' : 'NT'}
                  </span>
                  {locked ? (
                    <span className="path__lock" role="img" aria-label="Premium only">
                      <Icon name="lock" size={16} />
                    </span>
                  ) : null}
                </div>
                <p className="path__blurb">{path.blurb}</p>
                <div className="path__meter">
                  <div className="path__meter-track">
                    <div className="path__meter-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="path__meter-label">
                    {known}/{path.count} known
                  </span>
                </div>
              </button>
            </li>
          );
        })}
      </ul>

      {!isPremium ? (
        <p className="hint">
          Two paths are open on the free tier. Premium unlocks all {PATHS.length}.
        </p>
      ) : null}
    </section>
  );
}
